import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { Environment, IEnvironmentService } from '../types';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService implements IEnvironmentService {
  private env: Environment = {} as Environment;
  readonly APP_ENV_PATH = 'assets/environment.json';
  readonly APP_ENV_VERSION_PATH = 'assets/version.json';

  public env$: BehaviorSubject<Environment | null> =
    new BehaviorSubject<Environment | null>(null);

  constructor(private readonly http: HttpClient) {}

  get environment$() {
    return this.env$;
  }

  get features() {
    return this.env.feature;
  }

  get isProduction() {
    return EnvironmentService.isProduction;
  }

  get environmentName() {
    return this.env.name;
  }

  get baseUrl() {
    return this.env.api.baseUrl;
  }

  get aciUrl() {
    return this.env.application.aciUrl;
  }

  get patientUrl() {
    return this.env.application.patientUrl;
  }

  get expressUrl() {
    return this.env.application.expressUrl;
  }

  get preCheckUrl() {
    return this.env.application.preCheckUrl;
  }

  get apptSchedulingUrl() {
    return this.env.application.apptSchedulingUrl;
  }

  get appointmentSchedulingBaseUrl() {
    return this.env?.application?.appointmentSchedulingBaseUrl;
  }

  get adminApiBaseUrl() {
    return this.env?.application?.adminApiBaseUrl;
  }

  get rateVisitBaseUrl() {
    return this.env?.application?.rateVisitBaseUrl;
  }

  get moonshotUrl() {
    return this.env.application.moonshotUrl;
  }

  /**
   * Base URL of the Moonshot AI gateway for the Orbi agent. `undefined`/empty
   * means the feature is disabled for this environment (parity with mobile).
   */
  get orbiApiBaseUrl() {
    return this.env?.application?.orbiApiBaseUrl;
  }

  /**
   * Local/dev default for the Orbi `web-ai-agent` Split flag when Split.io has
   * no treatment. `undefined` in deployed envs ⇒ agent stays OFF.
   */
  get orbiAgentDefault(): boolean {
    return this.env?.application?.orbiAgentDefault ?? false;
  }

  get onDemandUrl() {
    return this.env.application.onDemandUrl;
  }

  get userAgreementUrl() {
    return this.env.application.userAgreementUrl;
  }

  get smartyStreetsBaseUrl() {
    return this.env.api.smartyStreetsBaseUrl;
  }

  get smartyStreetsSiteKey() {
    return this.env.api.smartyStreetsSiteKey;
  }

  get paymentContextUrl() {
    return this.env.api.paymentContextUrl;
  }

  get paymentCallbackUrl() {
    return this.env.api.paymentCallbackUrl;
  }

  get paymentSource() {
    return this.env.api.paymentSource;
  }

  get recaptchaKey() {
    return this.env.google.recaptchaKey;
  }

  get googleMapsKey() {
    return this.env.google.mapsKey;
  }

  get oktaIssuer() {
    return this.env.okta.issuer;
  }

  get oktaClientId() {
    return this.env.okta.clientId;
  }

  get ondemandClientId() {
    return this.env.okta.ondemandClientID;
  }

  get adobeScript() {
    return this.env.scripts.adobe;
  }

  // Expose Split.io configuration and script URL in a typed way
  get splitConfig() {
    return this.env.splitio;
  }

  get splitScriptUrl() {
    return this.env.scripts.split;
  }

  get algolia() {
    return this.env.algolia;
  }

  get appVersion() {
    return this.env.version || '';
  }

  async loadEnvironment() {
    if (!this.http) {
      console.error('HttpClient not provided. Environment loading skipped.');
      return;
    }

    this.env = await firstValueFrom(
      combineLatest([
        this.http.get<Environment>(this.APP_ENV_PATH),
        this.http.get<Environment>(this.APP_ENV_VERSION_PATH),
      ]).pipe(
        map(
          ([appEnvRes, appEnvVerRes]) =>
            ({
              ...appEnvRes,
              ...appEnvVerRes,
            } as Environment)
        )
      )
    );
    this.env$.next(this.env);
    // Load Adobe script
    const adobeScript = document.createElement('script');
    adobeScript.defer = true;
    adobeScript.src = this.adobeScript;
    document.head.appendChild(adobeScript);

    // Optionally load Split.io SDK via CDN if authorizationKey is provided
    try {
      const splitCfg = this.env.splitio;
      if (splitCfg?.authorizationKey) {
        const splitUrl =
          this.env.scripts.split ||
          'https://cdn.split.io/sdk/split-10.25.0.min.js';
        const splitScript = document.createElement('script');
        splitScript.async = true;
        splitScript.src = splitUrl;
        document.head.appendChild(splitScript);
      }
    } catch {
      // no-op
    }
  }

  loadEnvironmentOkta() {
    return combineLatest([
      this.http.get<Environment>('assets/environment.json'),
      this.http.get<Environment>('assets/version.json'),
    ])
      .pipe(
        map(
          ([appEnv, appEnvVer]) => ({ ...appEnv, ...appEnvVer } as Environment)
        ),
        shareReplay(1)
      )
      .toPromise();
  }

  static get isProduction() {
    return !isDevMode();
  }
}
